"use client";

import { useState, useRef, useEffect } from "react";
import BFLImage from "@repo/shared-ui/BFLImage";
import {
  getPageType,
  getUtmParamsFromCookie,
  redirectWithUtmParam,
} from "@repo/shared-utils/common-utils";
import { INVEST_NOW_MODAL_KEY } from "@repo/shared-utils/shared.constants";
// API function to send OTP
const sendMobileOtp = async (mobileNumber: string) => {
  const { bfslSendOtp } = await import("@repo/sso/bfslLoginApi");
  const utm = getUtmParamsFromCookie();
  const payload = {
    name: "",
    mobile: mobileNumber,
    refferalCode: "",
    utmCampaign: utm?.bflpageType || getPageType("tag-1") || "NA",
  };

  try {
    const bfslSendOtpResponse = await bfslSendOtp(payload);
    return bfslSendOtpResponse;
  } catch (error) {
    console.error("Error sending OTP:", error);
    throw error;
  }
};

// API function to validate OTP
const validateMobileOtp = async (mobile: string, mobpswd: string) => {
  const { bfslVerifyOtp } = await import("@repo/sso/bfslLoginApi");
  const payload = {
    mobile: mobile,
    mobpswd: mobpswd,
    fullname: "",
    utmCampaign: "pdp",
  };

  try {
    const bfslVerifyOtpResponse = await bfslVerifyOtp(payload);
    return bfslVerifyOtpResponse;
  } catch (error) {
    console.error("Error sending OTP:", error);
    throw error;
  }
};

// API function to save lead
const saveLead = async (mobile: string) => {
  const { bfslSaveLead } = await import("@repo/sso/bfslLoginApi");
  const payload = {
    name: "",
    mobile: mobile,
    utmCampaign: "pdp",
  };

  try {
    const bfslSaveLeadResponse = await bfslSaveLead(payload);
    return bfslSaveLeadResponse;
  } catch (error) {
    console.error("Error saving lead:", error);
  }
};

interface IDematVerifyOtp {
  title: string;
  subtitle1: string;
  subtitle2: string;
  desc: string;
  getOtp: string;
  link: string;
  openInNewTab: string;
  resend: string;
  resendOtp: string;
  sending: string;
  submit: string;
  validating: string;
}

// Define the props for this component
interface DematOtpVerifyModalProps {
  onValidate?: (isValid: boolean, otpValue: string) => void;
  onValidationSuccess?: (otpValue: string) => void;
  onValidationFailure?: (otpValue: string) => void;
  mobileNumber?: string;
  ssoContent?: IDematVerifyOtp;
}

const BFLOtpVerifyModal = ({
  onValidate,
  onValidationSuccess,
  onValidationFailure,
  mobileNumber = "",
  ssoContent,
}: DematOtpVerifyModalProps = {}) => {
  const {
    link = "",
    resend = "",
    resendOtp = "",
    desc = "",
    submit = "",
    validating = "",
    sending = "",
  } = ssoContent ?? {};
  // State for OTP inputs
  const [otpValues, setOtpValues] = useState<string[]>(["", "", "", ""]);
  const [errors, setErrors] = useState<boolean>(false);
  const [resendTimer, setResendTimer] = useState<number>(30);
  const [canResend, setCanResend] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isResending, setIsResending] = useState<boolean>(false);

  // References for input fields
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Handle timer for resend OTP
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => {
        setResendTimer(resendTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [resendTimer]);

  // Handle OTP input change
  const handleChange = (index: number, value: string) => {
    // Only allow numeric values
    if (!/^\d*$/.test(value)) return;

    const newOtpValues = [...otpValues];
    // Only take the first digit
    newOtpValues[index] = value.slice(0, 1);
    setOtpValues(newOtpValues);

    // Clear errors when user types
    if (errors) {
      setErrors(false);
    }

    // Auto focus to next input
    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle key navigation (backspace)
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otpValues[index] && index > 0) {
      // Move focus to previous input when backspace is pressed on empty input
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Validate OTP
  const validateOtp = async () => {
    const isValid = otpValues.every((val) => val.trim() !== "");
    const otpCode = otpValues.join("");

    // If onValidate prop is provided, call it with validation result
    if (onValidate) {
      onValidate(isValid, otpCode);
    }

    if (!isValid) {
      setErrors(true);
      return false;
    }

    try {
      setIsLoading(true);
      setErrors(false);

      // Call OTP validation API
      const validationResponse = await validateMobileOtp(mobileNumber, otpCode);
      const validationResult = validationResponse as {
        statusCode?: number;
        status?: string;
        message?: string;
        data?: {
          Response?: {
            url?: string;
          };
        };
      };

      // Check if OTP validation was successful
      if (validationResult.statusCode === 90 && validationResult.status === "success") {
        // Call Save Lead mock before redirect
        await saveLead(mobileNumber);

        // Get the redirect URL from response
        const redirectUrl = validationResult.data?.Response?.url;

        // Add UTM parameters to the URL
        if (redirectUrl) {
          redirectWithUtmParam(redirectUrl, "true");
        }

        // Call success callback
        onValidationSuccess?.(otpCode);
      } else {
        setErrors(true);
        console.error("OTP validation failed:", validationResult.message);

        // Call failure callback
        onValidationFailure?.(otpCode);
      }
    } catch (error) {
      setErrors(true);
      console.error("Error validating OTP:", error);

      // Call failure callback
      onValidationFailure?.(otpCode);
    } finally {
      setIsLoading(false);
    }

    return isValid;
  };

  // Handle resend OTP
  const handleResend = async () => {
    if (canResend && !isResending) {
      try {
        setIsResending(true);

        // Call send OTP API
        const otpResponse = await sendMobileOtp(mobileNumber);
        const otpResult = otpResponse as { statusCode?: number; status?: string; message?: string };

        // Check if OTP was sent successfully
        if (otpResult.statusCode === 90 && otpResult.status === "success") {
          // Reset OTP values and timer on successful resend
          setOtpValues(["", "", "", ""]);
          setErrors(false);
          setResendTimer(30);
          setCanResend(false);
          inputRefs.current?.[0]?.focus();
        } else {
          console.error("Resend OTP API failed:", otpResponse.message);
        }
      } catch (error) {
        console.error("Error resending OTP:", error);
      } finally {
        setIsResending(false);
      }
    }
  };

  return (
    <div className="flex flex-col items-center">
      <BFLImage
        alt="Otp"
        className="otp-image"
        src={link}
        priority={false}
        loading="lazy"
        unoptimized={true}
        width={100}
        height={39}
      />
      <div className="otp-section mt-5 flex gap-2">
        {["first", "second", "third", "fourth"].map((position, i) => (
          <input
            key={`otp-${position}`}
            ref={(el) => {
              inputRefs.current[i] = el;
            }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            autoFocus={i === 0}
            value={otpValues[i]}
            aria-label={`OTP Digit ${i + 1}`}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            className={`common-input-class h-12 w-12 rounded border ${
              errors && !otpValues[i] ? "border-red-600" : "border-gray-300"
            } text-center focus:ring-2 focus:ring-blue-500 focus:outline-none`}
          />
        ))}
      </div>

      {errors && <div className="mt-2 text-sm text-red-600">{INVEST_NOW_MODAL_KEY.enterOtp}</div>}

      <div className="mt-2 text-xs text-gray-600">
        {canResend ? (
          <button
            className={`text-link-primary underline ${isResending ? "cursor-not-allowed opacity-50" : ""}`}
            disabled={isResending}
            onClick={async () => {
              await handleResend();
            }}
          >
            {isResending ? sending : resend}
          </button>
        ) : (
          <span>
            {resendOtp} {resendTimer}
            {resendTimer > 1 ? "s" : ""}
          </span>
        )}
      </div>

      <span className="gray-600 pt-5 text-xs">{desc}</span>

      <button
        className={`mt-4 cursor-pointer rounded-[200px] bg-gradient-to-r from-[#ff6700] to-[#ff8900] px-[78px] py-[11px] text-sm leading-[18px] font-medium text-white ${isLoading ? "cursor-not-allowed opacity-50" : ""}`}
        disabled={isLoading}
        onClick={async (e) => {
          e.preventDefault();
          await validateOtp();
        }}
      >
        {isLoading ? validating : submit}
      </button>
    </div>
  );
};

export default BFLOtpVerifyModal;
